/**
 * Client-side search, sort and view state for a list screen.
 *
 * Client-side on purpose. Every list in this portal is bounded by the size of one small
 * company — 33 employees, a few dozen sites, 200 shifts a month — so a round trip per
 * keystroke would add latency to solve a problem nobody has. The two endpoints that can
 * genuinely grow (shifts, audit) already filter server-side by date before this runs.
 *
 * If a list ever does outgrow this, the hook's shape is the same one a server-side
 * version would have, so the change stays inside the hook.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortOption<T> {
  /** Stable id, used in the select and in the persisted state. */
  key: string;
  /** Label key in the dictionary, e.g. `employees.name`. */
  labelKey: string;
  /** What to compare. Strings are compared with Italian collation, not ASCII. */
  value: (row: T) => string | number | null | undefined;
  /** Most lists want newest-first or worst-first by default. */
  defaultDirection?: SortDirection;
}

export interface TableStateOptions<T> {
  rows: T[];
  /** Fields the search box looks at. Anything not listed here is not searchable. */
  search?: (row: T) => (string | null | undefined)[];
  sorts?: SortOption<T>[];
  defaultSortKey?: string;
  /**
   * Persists view/sort choice per screen. Omit for lists where a remembered sort would
   * be more confusing than helpful.
   */
  storageKey?: string;
}

export type ViewMode = 'table' | 'cards';

/**
 * Lower-case and strip diacritics, so "Niccolo" finds "Niccolò" and "idoneita" finds
 * "Idoneità". Italian names and document titles are full of accents and nobody types
 * them into a search box.
 */
export function normalise(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

const collator = new Intl.Collator('it', { numeric: true, sensitivity: 'base' });

function compareValues(a: unknown, b: unknown): number {
  // Empty always sorts last, in both directions: "no expiry date" is not "the earliest
  // date", and a column of blanks at the top is never what anyone wanted.
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

interface Persisted {
  sortKey?: string;
  direction?: SortDirection;
  view?: ViewMode;
}

function readPersisted(storageKey?: string): Persisted {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(`hh.table.${storageKey}`);
    return raw ? (JSON.parse(raw) as Persisted) : {};
  } catch {
    return {};
  }
}

export interface TableState<T> {
  query: string;
  setQuery: (value: string) => void;
  sortKey: string;
  direction: SortDirection;
  /** Clicking the active column flips direction; a new column starts at its default. */
  toggleSort: (key: string) => void;
  setSort: (key: string, direction: SortDirection) => void;
  view: ViewMode;
  setView: (view: ViewMode) => void;
  /** Filtered and sorted. */
  rows: T[];
  /** Before filtering, so the UI can say "12 of 48". */
  total: number;
  isFiltered: boolean;
  clear: () => void;
  sorts: SortOption<T>[];
}

export function useTableState<T>({
  rows,
  search,
  sorts = [],
  defaultSortKey,
  storageKey,
}: TableStateOptions<T> & { sorts?: SortOption<T>[] }): TableState<T> {
  const persisted = useMemo(() => readPersisted(storageKey), [storageKey]);

  const initialKey = persisted.sortKey ?? defaultSortKey ?? sorts[0]?.key ?? '';
  const initialDirection =
    persisted.direction ?? sorts.find((s) => s.key === initialKey)?.defaultDirection ?? 'asc';

  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);
  const [view, setView] = useState<ViewMode>(persisted.view ?? 'table');

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(
        `hh.table.${storageKey}`,
        JSON.stringify({ sortKey, direction, view }),
      );
    } catch {
      // Not remembering a sort order is not worth failing a render over.
    }
  }, [storageKey, sortKey, direction, view]);

  const setSort = useCallback((key: string, next: SortDirection) => {
    setSortKey(key);
    setDirection(next);
  }, []);

  const toggleSort = useCallback(
    (key: string) => {
      if (key === sortKey) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortKey(key);
      setDirection(sorts.find((s) => s.key === key)?.defaultDirection ?? 'asc');
    },
    [sortKey, sorts],
  );

  const filtered = useMemo(() => {
    const needle = normalise(query);
    if (!needle || !search) return rows;

    // Every word has to match somewhere, so "ferrari sanit" finds Marco Ferrari's
    // medical certificate without the user guessing the field order.
    const words = needle.split(/\s+/).filter(Boolean);

    return rows.filter((row) => {
      const haystack = normalise(
        search(row)
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .join(' '),
      );
      return words.every((word) => haystack.includes(word));
    });
  }, [rows, query, search]);

  const sorted = useMemo(() => {
    const spec = sorts.find((s) => s.key === sortKey);
    if (!spec) return filtered;

    // Copy before sorting: the array belongs to the query cache.
    const copy = [...filtered];
    copy.sort((a, b) => {
      const result = compareValues(spec.value(a), spec.value(b));
      return direction === 'asc' ? result : -result;
    });
    return copy;
  }, [filtered, sorts, sortKey, direction]);

  const clear = useCallback(() => setQuery(''), []);

  return {
    query,
    setQuery,
    sortKey,
    direction,
    toggleSort,
    setSort,
    view,
    setView,
    rows: sorted,
    total: rows.length,
    isFiltered: query.trim().length > 0,
    clear,
    sorts,
  };
}
